import React from "react";
import { fetch } from "app/frame/Searchbar/fetch";
import { Suggest } from "app/frame/Searchbar/Searchbar";

/**
 * Returns a ref object that can be attached to a textfield. The textfield will receive focus
 * immediately.
 */
export const useAutofocus = () => {
    const inputRef = React.createRef() as React.RefObject<HTMLInputElement>;

    React.useEffect(() => {
        if (inputRef && inputRef.current) {
            inputRef.current.focus();
        }
    }, [inputRef.current]);

    return inputRef;
};

interface Result {
    suggestions: Suggest;
}

export interface SuggestionObject {
    title: string;
    htmlTitle: string;
    url: string;
}

const getSearchURL = (searchValue: string) =>
    `//suggest.t-online.de/www_suggestion_portal_group/ariadne/suggestion?context=start&q=${searchValue}&callback=%JSONPFUNC%`;

const processResponse = (response: Result, searchString: string): SuggestionObject[] => {
    if (!response || !response.suggestions || !response.suggestions.Suggests) {
        return [];
    }

    const result: SuggestionObject[] = response.suggestions.Suggests.map(suggest => ({
        title: suggest.suggest[2],
        htmlTitle: suggest.suggest[0],
        url: `https://suche.t-online.de/fast-cgi/tsc?sr=pto_sugg&q=${suggest.suggest[2]}`
    }));

    result.push(createFinalSuggest(searchString));

    return result;
};

const createFinalSuggest = (searchString: string): SuggestionObject => ({
    title: searchString,
    htmlTitle: `Nach <b>${searchString}</b> suchen`,
    url: `https://suche.t-online.de/fast-cgi/tsc?sr=pto_sugg_kw&q=${searchString}`
});

/**
 * Will perform a request to the search suggestions API and return an array of suggestion objects.
 */
export const useAutosuggest = (searchValue: string): SuggestionObject[] => {
    const [autosuggests, setAutosuggests] = React.useState<SuggestionObject[]>([]);
    const fetchTimeout: React.MutableRefObject<NodeJS.Timeout | null> = React.useRef(null);

    React.useEffect(() => {
        if (fetchTimeout.current) {
            clearTimeout(fetchTimeout.current);
        }
        if (searchValue.length) {
            fetchTimeout.current = setTimeout(() => {
                fetch({
                    url: getSearchURL(searchValue),
                    cache: true,
                    jsonp: true,
                    success: (result: Result) => {
                        setAutosuggests(processResponse(result, searchValue));
                    }
                });
            }, 100);
        } else {
            setAutosuggests([]);
        }
    }, [searchValue]);

    return autosuggests;
};
